import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type {
  ArtifactPendingSuggestion,
  BidArtifact,
  CreateBidArtifact,
  UpdateBidArtifact,
} from '@/lib/api'
import {
  listArtifacts,
  getArtifact,
  createArtifact,
  updateArtifact,
  deleteArtifact,
  reopenArtifact,
} from '@/lib/api'

// ===================
// Types
// ===================

type ArtifactStatus = BidArtifact['status']

interface ResponsesState {
  // State
  artifacts: BidArtifact[]
  selectedArtifact: BidArtifact | null
  /**
   * Staged suggestions waiting on the document `fetchArtifactById` last loaded
   * (R1 W2), tagged with its id so a list never shows under another document.
   */
  selectedArtifactPendingSuggestions: { artifactId: string; items: ArtifactPendingSuggestion[] } | null
  currentBidId: string | null
  isLoading: boolean
  error: string | null
  filter: {
    status?: ArtifactStatus
    search?: string
  }

  // Actions
  fetchArtifacts: (bidId: string) => Promise<void>
  /** Load the bid's artifacts unless they are already loaded or in flight.
   *  Use from components that merely *need* the list (table cells, pickers);
   *  use `fetchArtifacts` when a refresh is the point. */
  ensureArtifacts: (bidId: string) => Promise<void>
  fetchArtifactById: (id: string) => Promise<void>
  /**
   * Re-read only the suggestions waiting on a document (after a job that may
   * have held some back). Leaves `selectedArtifact`, `isLoading` and `error`
   * alone so a background refresh can't overwrite what the page is showing; on
   * failure the current list stays.
   */
  refreshPendingSuggestions: (id: string) => Promise<void>
  createArtifact: (bidId: string, data: CreateBidArtifact) => Promise<BidArtifact>
  updateArtifact: (id: string, data: UpdateBidArtifact) => Promise<void>
  deleteArtifact: (id: string) => Promise<void>
  reopenArtifact: (id: string) => Promise<void>
  /** Replace the cached BidArtifact (selectedArtifact + list entry). Used after
   *  review-workflow transitions, where the handler returns the updated artifact. */
  upsertArtifact: (artifact: BidArtifact) => void
  selectArtifact: (artifact: BidArtifact | null) => void
  setFilter: (filter: ResponsesState['filter']) => void
  clearError: () => void
  reset: () => void

  // Computed
  getArtifactsByStatus: (status: ArtifactStatus) => BidArtifact[]
  getFilteredArtifacts: () => BidArtifact[]
}

// ===================
// Fetch de-duplication (module state, outside Zustand)
// ===================

// One submission-checklist row = one <ResponseLinkCell>, and every one of them
// asks for the artifact list on mount. Without these guards a table of N rows
// issued N identical GET /bids/{id}/artifacts calls back to back, and a bid
// with no responses yet re-issued them on every remount (F6, second instance).
//
// The guards belong to `ensureArtifacts` alone. `fetchArtifacts` stays an
// unconditional refresh — de-duplicating it would swallow deliberate reloads.
let inFlightBidId: string | null = null
let inFlightFetch: Promise<void> | null = null
let loadedBidId: string | null = null
// Monotonic id so a slow response for bid A can't clobber a newer one for bid B.
let artifactsRequestId = 0

// ===================
// Store
// ===================

export const useResponsesStore = create<ResponsesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      artifacts: [],
      selectedArtifact: null,
      selectedArtifactPendingSuggestions: null,
      currentBidId: null,
      isLoading: false,
      error: null,
      filter: {},

      // Fetch all artifacts for a bid — always hits the network.
      fetchArtifacts: async (bidId: string) => {
        const requestId = ++artifactsRequestId
        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const response = await listArtifacts(bidId)
          // A newer request has superseded this one (bid switched mid-flight).
          if (requestId !== artifactsRequestId) return
          set({ artifacts: response.artifacts, isLoading: false })
          loadedBidId = bidId
        } catch (error) {
          if (requestId !== artifactsRequestId) return
          loadedBidId = null
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch artifacts',
            isLoading: false,
          })
        }
      },

      // Load once per bid — an empty result is still a loaded result. Concurrent
      // callers (one per table row) share the single in-flight request.
      ensureArtifacts: async (bidId: string) => {
        if (loadedBidId === bidId) return
        if (inFlightFetch && inFlightBidId === bidId) return inFlightFetch

        const request = get()
          .fetchArtifacts(bidId)
          .finally(() => {
            if (inFlightBidId === bidId) {
              inFlightBidId = null
              inFlightFetch = null
            }
          })

        inFlightBidId = bidId
        inFlightFetch = request
        return request
      },

      // Fetch single artifact by ID
      fetchArtifactById: async (id: string) => {
        try {
          set({ isLoading: true, error: null })
          const response = await getArtifact(id)
          set({
            selectedArtifact: response.artifact,
            selectedArtifactPendingSuggestions: {
              artifactId: response.artifact.id,
              items: Array.isArray(response.pendingSuggestions) ? response.pendingSuggestions : [],
            },
            isLoading: false,
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch artifact',
            isLoading: false,
          })
        }
      },

      // Re-read the suggestions list only. The overview endpoint is the one
      // source for it, so this reads the artifact and keeps just that part.
      refreshPendingSuggestions: async (id: string) => {
        try {
          const response = await getArtifact(id)
          set({
            selectedArtifactPendingSuggestions: {
              artifactId: response.artifact.id,
              items: Array.isArray(response.pendingSuggestions) ? response.pendingSuggestions : [],
            },
          })
        } catch {
          // Background refresh: keep the list we have; the next load corrects it.
        }
      },

      // Create new artifact
      createArtifact: async (bidId: string, data: CreateBidArtifact) => {
        try {
          set({ isLoading: true, error: null })
          const response = await createArtifact(bidId, data)
          set((state) => ({
            artifacts: [...state.artifacts, response.artifact],
            isLoading: false,
          }))
          return response.artifact
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create artifact',
            isLoading: false,
          })
          throw error
        }
      },

      // Update existing artifact
      updateArtifact: async (id: string, data: UpdateBidArtifact) => {
        try {
          set({ isLoading: true, error: null })
          const response = await updateArtifact(id, data)
          set((state) => ({
            artifacts: state.artifacts.map((a) =>
              a.id === response.artifact.id ? response.artifact : a
            ),
            selectedArtifact:
              state.selectedArtifact?.id === response.artifact.id
                ? response.artifact
                : state.selectedArtifact,
            isLoading: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update artifact',
            isLoading: false,
          })
          throw error
        }
      },

      // Delete artifact
      deleteArtifact: async (id: string) => {
        try {
          set({ isLoading: true, error: null })
          await deleteArtifact(id)
          set((state) => ({
            artifacts: state.artifacts.filter((a) => a.id !== id),
            selectedArtifact: state.selectedArtifact?.id === id ? null : state.selectedArtifact,
            isLoading: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete artifact',
            isLoading: false,
          })
          throw error
        }
      },

      // Replace a cached artifact (used after submit/decide/cancel-review).
      upsertArtifact: (artifact: BidArtifact) => {
        set((state) => ({
          artifacts: state.artifacts.some((a) => a.id === artifact.id)
            ? state.artifacts.map((a) => (a.id === artifact.id ? artifact : a))
            : [...state.artifacts, artifact],
          selectedArtifact:
            state.selectedArtifact?.id === artifact.id ? artifact : state.selectedArtifact,
        }))
      },

      // Reopen a finalized artifact
      reopenArtifact: async (id: string) => {
        try {
          set({ error: null })
          const response = await reopenArtifact(id)
          set((state) => ({
            artifacts: state.artifacts.map((a) =>
              a.id === id ? response.artifact : a
            ),
            selectedArtifact:
              state.selectedArtifact?.id === id
                ? response.artifact
                : state.selectedArtifact,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to reopen artifact',
          })
          throw error
        }
      },

      // Select an artifact for detail view
      selectArtifact: (artifact: BidArtifact | null) => {
        set({ selectedArtifact: artifact })
      },

      // Set filter criteria
      setFilter: (filter: ResponsesState['filter']) => {
        set({ filter })
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store (e.g., when switching bids)
      reset: () => {
        loadedBidId = null
        inFlightBidId = null
        inFlightFetch = null
        // Orphan any in-flight response so it can't repopulate after the reset.
        artifactsRequestId++
        set({
          artifacts: [],
          selectedArtifact: null,
          selectedArtifactPendingSuggestions: null,
          currentBidId: null,
          isLoading: false,
          error: null,
          filter: {},
        })
      },

      // Get artifacts by status
      getArtifactsByStatus: (status: ArtifactStatus) => {
        return get().artifacts.filter((a) => a.status === status)
      },

      // Get filtered artifacts
      getFilteredArtifacts: () => {
        const { artifacts, filter } = get()
        return artifacts.filter((artifact) => {
          if (filter.status && artifact.status !== filter.status) return false
          if (filter.search) {
            const search = filter.search.toLowerCase()
            return (
              artifact.name.toLowerCase().includes(search) ||
              artifact.description?.toLowerCase().includes(search)
            )
          }
          return true
        })
      },
    }),
    { name: 'responses-store' }
  )
)
